import { GlColumnDef, GlSheetRowVO, buildColumnKey } from '@/services/accountService/glInvoiceService';
import { sumByKeys } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';

/** 是否为 BS-NCA-PPE 下的 PPE 列 */
export function isPpeColumn(def: GlColumnDef): boolean {
    return def.fsCode === 'BS-NCA-PPE';
}

/** 名称是否为累计折旧类 */
export function isPpeDepreciationName(nameEn: string | null | undefined): boolean {
    const n = (nameEn || '').trim().toLowerCase();
    return n.includes('depreciation')
        || n.startsWith('acc dep')
        || n.startsWith('accum dep')
        || n.startsWith('acc. dep')
        || n.startsWith('accum. dep');
}

/**
 * 去除折旧前缀，便于 cost / acc dep 归入同一组。
 * "Acc dep - Furniture and equipment" → "furniture and equipment"
 */
export function stripPpeDepreciationPrefix(nameEn: string | null | undefined): string {
    if (!nameEn) return '';
    return nameEn
        .trim()
        .replace(
            /^(accumulated\s+depreciation|accum\.?\s*dep|acc\.?\s*dep)\s*[-–—]\s*/i,
            '',
        )
        .trim();
}

export interface PpeGroup {
    /** 分组 key：ppeCategory 或归一化后的基础名称 */
    groupKey: string;
    /** 展示标签，如 "Furniture and equipment" */
    groupLabel: string;
    columns: GlColumnDef[];
    columnKeys: string[];
}

function resolvePpeGroupKey(def: GlColumnDef): string {
    const cat = (def.ppeCategory || '').trim();
    if (cat) return cat;
    const base = stripPpeDepreciationPrefix(def.nameEn).toLowerCase();
    return base || buildColumnKey(def);
}

function resolvePpeGroupLabel(columns: GlColumnDef[], groupKey: string): string {
    const costCol = columns.find((c) => !isPpeDepreciationName(c.nameEn));
    if (costCol?.nameEn?.trim()) return costCol.nameEn.trim();

    const depCol = columns.find((c) => isPpeDepreciationName(c.nameEn));
    const stripped = stripPpeDepreciationPrefix(depCol?.nameEn);
    if (stripped) return stripped;

    // camelCase ppeCategory → Title Case
    if (/^[a-z]+([A-Z][a-z]*)*$/.test(groupKey)) {
        return groupKey
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (c) => c.toUpperCase())
            .trim();
    }
    return groupKey;
}

/**
 * 按 ppeCategory（缺省时按去折旧前缀后的 nameEn）分组。
 * 同一组通常含 Cost + Acc dep 两列。
 */
export function buildPpeGroups(defs: GlColumnDef[]): PpeGroup[] {
    const ppeDefs = defs.filter(isPpeColumn).sort((a, b) => a.columnIndex - b.columnIndex);
    if (ppeDefs.length === 0) return [];

    const groupMap = new Map<string, GlColumnDef[]>();
    const order: string[] = [];

    for (const d of ppeDefs) {
        const key = resolvePpeGroupKey(d);
        let cols = groupMap.get(key);
        if (!cols) {
            cols = [];
            groupMap.set(key, cols);
            order.push(key);
        }
        cols.push(d);
    }

    return order.map((groupKey) => {
        const columns = groupMap.get(groupKey)!;
        // Cost 列在前，Acc dep 在后
        columns.sort((a, b) => {
            const aDep = isPpeDepreciationName(a.nameEn) ? 1 : 0;
            const bDep = isPpeDepreciationName(b.nameEn) ? 1 : 0;
            if (aDep !== bDep) return aDep - bDep;
            return a.columnIndex - b.columnIndex;
        });
        return {
            groupKey,
            groupLabel: resolvePpeGroupLabel(columns, groupKey),
            columns,
            columnKeys: columns.map((c) => buildColumnKey(c)),
        };
    });
}

/** 表头短标签：Cost / Acc. dep. */
export function ppeColumnShortLabel(def: GlColumnDef): string {
    if (isPpeDepreciationName(def.nameEn)) return 'Acc. dep.';
    return 'Cost';
}

/** 过滤组内全空列（当期汇总、上年余额、明细行任一非零即保留） */
export function filterNonEmptyPpeColumns(
    group: PpeGroup,
    rows: GlSheetRowVO[],
    summaryCurrent: Record<string, number>,
    summaryPrior: Record<string, number> = {},
): GlColumnDef[] {
    return group.columns.filter((col) => {
        const key = buildColumnKey(col);
        if ((summaryCurrent[key] ?? 0) !== 0) return true;
        if ((summaryPrior[key] ?? 0) !== 0) return true;
        return rows.some((row) => {
            const v = row.columnValues?.[key];
            return v != null && v !== 0;
        });
    });
}

/** 组内当期变动合计 */
export function sumPpeGroupMovement(group: PpeGroup, summary: Record<string, number>): number {
    return sumByKeys(summary, group.columnKeys);
}
